import { supabase } from "@/lib/supabase"
import type { ChatMessage } from "@/types/chat"

// ─── Webhook URLs ─────────────────────────────────────────────────────────────
// Pro/Teams: RAG completo + tools (Supabase, facturas, métricas en tiempo real)
const PRO_WEBHOOK_URL = import.meta.env.VITE_N8N_WEBHOOK_URL
    || "https://n8n.cheosdesign.info/webhook/temporaPro"

// Free (Support Widget): RAG ligero, solo FAQ y documentación. Sin acceso a datos.
const SUPPORT_WEBHOOK_URL = import.meta.env.VITE_N8N_SUPPORT_WEBHOOK_URL
    || "https://n8n.cheosdesign.info/webhook/temporaSoporte"

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function fetchHistory(userId: string, conversationId: string): Promise<ChatMessage[]> {
    try {
        const { data, error } = await supabase
            .from('chat_messages')
            .select('id, text, sender, created_at, image_url, conversation_id')
            .eq('user_id', userId)
            .eq('conversation_id', conversationId)
            .order('created_at', { ascending: true })

        if (error) {
            console.error("Error fetching chat history:", error)
            return []
        }

        return data.map(m => ({
            id: m.id,
            text: m.text,
            sender: m.sender as 'user' | 'bot',
            timestamp: new Date(m.created_at),
            imageUrl: m.image_url ?? undefined,
        }))
    } catch (err) {
        console.error("Unexpected error fetching history:", err)
        return []
    }
}

async function saveMessage(
    userId: string,
    conversationId: string,
    sender: 'user' | 'bot',
    text: string,
    imageUrl?: string,
): Promise<string | null> {
    const { data, error } = await supabase
        .from('chat_messages')
        .insert({
            user_id: userId,
            sender,
            text: text.trim(),
            conversation_id: conversationId,
            image_url: imageUrl ?? null,
        })
        .select('id')
        .single()

    if (error) {
        console.error("Error saving message:", error)
        return null
    }
    return data.id
}

function generateSafeId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try {
      return crypto.randomUUID();
    } catch (_e) {
      // Ignorar e ir al fallback
    }
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

function buildErrorMessage(language: string = 'es'): ChatMessage {
    const text = language === 'en' 
        ? "I'm sorry, I can't connect to the server right now. Please try again later or contact us by email."
        : "Lo siento, no puedo conectar con el servidor en este momento. Por favor, intenta más tarde o contáctanos por email."
    
    return {
        id: generateSafeId(),
        text,
        sender: 'bot',
        timestamp: new Date(),
        isError: true,
    }
}

// ─── chatService ──────────────────────────────────────────────────────────────

export const chatService = {
    /**
     * Obtiene el historial de chat del usuario para una conversación dada.
     */
    getChatHistory: fetchHistory,

    /**
     * [PRO / TEAMS] Envía un mensaje a Jarvis con todas las capacidades:
     * análisis de datos, generación de documentos, imágenes, memoria persistente.
     *
     * Usado por: AiAssistant.tsx (ruta protegida por PaywallGate)
     */
    async sendProMessage(
        text: string,
        userId: string,
        conversationId: string,
        organizationId?: string,
        imageUrl?: string,
        history?: ChatMessage[],
        language: string = 'es'
    ): Promise<ChatMessage> {
        try {
            // 1. Guardar mensaje del usuario
            await saveMessage(userId, conversationId, 'user', text, imageUrl)

            // 2. Enviar al webhook Pro de n8n (flujo completo con tools)
            const response = await fetch(PRO_WEBHOOK_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    message: text,
                    userId,
                    organizationId,
                    conversationId,
                    imageUrl: imageUrl ?? null,
                    timestamp: new Date().toISOString(),
                    planTier: "pro",
                    context: {
                        page: window.location.pathname,
                        platform: "web",
                        language,
                        history: history?.map(m => ({
                            role: m.sender === 'user' ? 'user' : 'assistant',
                            content: m.text,
                        })) ?? [],
                    },
                }),
            })

            if (!response.ok) throw new Error(`n8n Pro responded with ${response.status}`)

            const data = await response.json()
            const responseData = Array.isArray(data) ? data[0] : data
            const botText = responseData?.output
                || responseData?.text
                || "No he recibido una respuesta válida. Intenta de nuevo."

            // 3. Guardar respuesta del bot
            const { data: saved, error: saveErr } = await supabase
                .from('chat_messages')
                .insert({
                    user_id: userId,
                    sender: 'bot',
                    text: botText,
                    conversation_id: conversationId,
                })
                .select('id, created_at')
                .single()

            if (saveErr) throw saveErr

            return {
                id: saved.id,
                text: botText,
                sender: 'bot',
                timestamp: new Date(saved.created_at),
                isError: false,
            }
        } catch (err) {
            console.error("Pro Chat Error:", err)
            return buildErrorMessage(language)
        }
    },

    /**
     * [FREE — Soporte básico] Envía un mensaje al bot de soporte ligero.
     * Solo acceso a RAG de documentación. Sin acceso a datos de la organización.
     * Sin herramientas (tools) de Supabase, análisis ni generación de documentos.
     *
     * Usado por: SupportWidget.tsx (accesible para todos los planes)
     */
    async sendSupportMessage(
        text: string,
        userId: string,
        conversationId: string,
        organizationId?: string,
        imageUrl?: string,
        history?: ChatMessage[],
        requestHuman?: boolean,
        language: string = 'es'
    ): Promise<ChatMessage> {
        try {
            // 1. Guardar mensaje del usuario
            await saveMessage(userId, conversationId, 'user', text, imageUrl)

            // 2. Enviar al webhook de soporte de n8n (flujo ligero, solo RAG)
            const response = await fetch(SUPPORT_WEBHOOK_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    message: text,
                    userId,
                    organizationId,
                    conversationId,
                    imageUrl: imageUrl ?? null,
                    timestamp: new Date().toISOString(),
                    requestHuman: requestHuman ?? false,
                    planTier: "free",
                    context: {
                        page: window.location.pathname,
                        platform: "web",
                        language,
                        // Historial limitado — solo las últimas 5 interacciones
                        history: history?.slice(-5).map(m => ({
                            role: m.sender === 'user' ? 'user' : 'assistant',
                            content: m.text,
                        })) ?? [],
                    },
                }),
            })

            if (!response.ok) throw new Error(`n8n Support responded with ${response.status}`)

            const data = await response.json()
            const responseData = Array.isArray(data) ? data[0] : data
            const botText = responseData?.output
                || responseData?.text
                || "No he recibido una respuesta válida. Intenta de nuevo."

            // 3. Guardar respuesta del bot
            const { data: saved, error: saveErr } = await supabase
                .from('chat_messages')
                .insert({
                    user_id: userId,
                    sender: 'bot',
                    text: botText,
                    conversation_id: conversationId,
                })
                .select('id, created_at')
                .single()

            if (saveErr) throw saveErr

            return {
                id: saved.id,
                text: botText,
                sender: 'bot',
                timestamp: new Date(saved.created_at),
                isError: false,
            }
        } catch (err) {
            console.error("Support Chat Error:", err)
            return buildErrorMessage(language)
        }
    },

    /**
     * Elimina todo el historial de chat del usuario (independiente del conversationId).
     */
    async clearChatHistory(userId: string): Promise<boolean> {
        try {
            const { error } = await supabase
                .from('chat_messages')
                .delete()
                .eq('user_id', userId)

            if (error) throw error
            return true
        } catch (err) {
            console.error("Error clearing chat history:", err)
            return false
        }
    },

    /**
     * @deprecated Usar sendProMessage o sendSupportMessage según el contexto.
     * Mantenido temporalmente para retrocompatibilidad.
     */
    sendMessage(
        text: string,
        userId: string,
        conversationId: string,
        organizationId?: string,
        imageUrl?: string,
        history?: ChatMessage[],
        requestHuman?: boolean,
    ) {
        return this.sendSupportMessage(text, userId, conversationId, organizationId, imageUrl, history, requestHuman)
    },
}
